"use client";

import { useEffect, useId, useRef, useState } from "react";
import type { PageMedia } from "../../types";
import {
  IMAGE_UPLOAD_TYPES,
  mediaLibraryUrl,
  mediaResponseError,
  mergeMedia,
  readMediaPage,
  uploadedMedia,
  type LibraryMedia,
} from "./mediaLibrary";
import styles from "./MediaField.module.css";

interface MediaFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  media: PageMedia[];
  /** Registers both newly uploaded images and existing images selected from the library. */
  onCreated: (media: PageMedia) => void;
  readOnly?: boolean;
}

/** Reset pending requests and local upload state when Puck selects another image field. */
export function MediaField(props: MediaFieldProps) {
  return <MediaFieldEditor key={props.id} {...props} />;
}

function MediaFieldEditor({
  id: fieldId,
  value,
  onChange,
  media,
  onCreated,
  readOnly,
}: MediaFieldProps) {
  // Puck mounts hidden and visible inspectors for the same field. Labels must
  // address this copy, rather than the first matching input elsewhere in the DOM.
  const id = `${fieldId}-${useId()}`;
  const [known, setKnown] = useState<LibraryMedia[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [images, setImages] = useState<LibraryMedia[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [retry, setRetry] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const uploadRequest = useRef<AbortController | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const selected = mergeMedia<LibraryMedia>(media, known).find((item) => item.id === value);
  const disabled = readOnly || uploading;

  useEffect(() => {
    if (!open || readOnly) return;
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setLibraryError("");
    const timer = setTimeout(
      async () => {
        try {
          const response = await fetch(mediaLibraryUrl(search, page), {
            credentials: "same-origin",
            signal: controller.signal,
          });
          const result: unknown = await response.json().catch(() => null);
          const responsePage = readMediaPage(result);
          if (!response.ok || !responsePage) {
            throw new Error(mediaResponseError(result, "The image library could not be loaded."));
          }
          if (!active) return;
          setImages((current) =>
            page === 1 ? responsePage.docs : mergeMedia(current, responsePage.docs)
          );
          setHasNextPage(responsePage.hasNextPage);
        } catch (cause) {
          if (active && !controller.signal.aborted) {
            setLibraryError(
              cause instanceof Error ? cause.message : "The image library could not be loaded."
            );
          }
        } finally {
          if (active) setLoading(false);
        }
      },
      search.trim() ? 200 : 0
    );
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, page, search, readOnly, retry]);

  useEffect(() => {
    if (readOnly) {
      uploadRequest.current?.abort();
      setUploading(false);
    }
    return () => uploadRequest.current?.abort();
  }, [readOnly]);

  function choose(image: LibraryMedia) {
    setKnown((current) => mergeMedia(current, [image]));
    onCreated(image);
    onChange(image.id);
    setOpen(false);
  }

  async function upload() {
    if (!file || !alt.trim() || disabled) return;
    const controller = new AbortController();
    uploadRequest.current = controller;
    setUploading(true);
    setUploadError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("_payload", JSON.stringify({ alt: alt.trim() }));
      const response = await fetch("/api/payload/media", {
        method: "POST",
        credentials: "same-origin",
        signal: controller.signal,
        body,
      });
      const result: unknown = await response.json().catch(() => null);
      const image = uploadedMedia(result);
      if (!response.ok || !image) {
        throw new Error(mediaResponseError(result, "The image could not be uploaded."));
      }
      if (controller.signal.aborted) return;
      choose(image);
      setFile(null);
      setAlt("");
      if (fileInput.current) fileInput.current.value = "";
    } catch (cause) {
      if (!controller.signal.aborted) {
        setUploadError(cause instanceof Error ? cause.message : "The image could not be uploaded.");
      }
    } finally {
      // No selection callback may run after switching to another Puck field or becoming read-only.
      if (!controller.signal.aborted) setUploading(false);
    }
  }

  return (
    <div className={styles.field} role="group" aria-labelledby={`${id}-label`}>
      <strong id={`${id}-label`}>Image</strong>
      {selected ? (
        <figure className={styles.preview}>
          <img src={selected.url} alt={selected.alt} loading="lazy" />
          <figcaption>{selected.alt || selected.filename || "Selected image"}</figcaption>
        </figure>
      ) : (
        <p className={styles.hint}>
          {value
            ? "The selected image is unavailable."
            : "Choose an image from your media library."}
        </p>
      )}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          aria-expanded={open && !readOnly}
          aria-controls={`${id}-library`}
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? "Close image library" : value ? "Change image" : "Choose image"}
        </button>
        {value && (
          <button
            type="button"
            className={styles.button}
            disabled={disabled}
            onClick={() => onChange("")}
          >
            Clear image
          </button>
        )}
      </div>
      {open && !readOnly && (
        <section id={`${id}-library`} aria-label="Image library" className={styles.library}>
          <div>
            <label htmlFor={`${id}-search`}>Search images</label>
            <input
              id={`${id}-search`}
              className={styles.input}
              type="search"
              value={search}
              placeholder="Search descriptions or filenames"
              disabled={uploading}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
                setImages([]);
                setHasNextPage(false);
              }}
            />
          </div>
          <div className={styles.grid} aria-label="Available images" aria-busy={loading}>
            {images.map((image) => {
              const name = image.alt || image.filename || "Untitled image";
              return (
                <button
                  key={image.id}
                  type="button"
                  className={styles.tile}
                  aria-label={`Select ${name}`}
                  aria-pressed={image.id === value}
                  disabled={uploading}
                  title={image.filename ? `${name} (${image.filename})` : name}
                  onClick={() => choose(image)}
                >
                  <img src={image.url} alt="" loading="lazy" />
                  <span>{name}</span>
                </button>
              );
            })}
          </div>
          {loading && (
            <p role="status" className={styles.hint}>
              Loading images…
            </p>
          )}
          {!loading && !libraryError && images.length === 0 && (
            <p className={styles.hint}>
              {search.trim() ? "No images match your search." : "No images in the library yet."}
            </p>
          )}
          {libraryError && (
            <>
              <p role="alert" className={styles.error}>
                {libraryError}
              </p>
              <button
                type="button"
                className={styles.button}
                onClick={() => setRetry((value) => value + 1)}
              >
                Retry loading images
              </button>
            </>
          )}
          {hasNextPage && !libraryError && (
            <button
              type="button"
              className={styles.button}
              disabled={loading || uploading}
              onClick={() => setPage((current) => current + 1)}
            >
              Load more images
            </button>
          )}
        </section>
      )}
      <div className={styles.upload}>
        <label htmlFor={`${id}-upload`}>Upload a new image</label>
        <input
          ref={fileInput}
          id={`${id}-upload`}
          type="file"
          accept={IMAGE_UPLOAD_TYPES}
          disabled={disabled}
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setUploadError("");
          }}
        />
        {file && (
          <>
            <div>
              <label htmlFor={`${id}-alt`}>Image description (alt text)</label>
              <input
                id={`${id}-alt`}
                className={styles.input}
                value={alt}
                disabled={disabled}
                onChange={(event) => setAlt(event.target.value)}
                aria-describedby={`${id}-alt-help`}
                placeholder="Describe what is in the image"
                required
              />
            </div>
            <p id={`${id}-alt-help`} className={styles.hint}>
              Describe the image for people using screen readers.
            </p>
            <button
              type="button"
              className={`${styles.button} ${styles.primary}`}
              disabled={disabled || !alt.trim()}
              onClick={() => void upload()}
            >
              {uploading ? "Uploading…" : "Upload and use image"}
            </button>
          </>
        )}
        {uploadError && (
          <p role="alert" className={styles.error}>
            {uploadError}
          </p>
        )}
      </div>
    </div>
  );
}
